import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  LinearProgress,
} from '@material-ui/core'

import { setConnSignalR } from '../../../redux/signalR/actions'
import {
  deleteConversations,
  setConversations,
  setMessages,
} from '../../../redux/conversations/actions'
import { setActiveConversation } from '../../../redux/activeConversation/actions'
import {
  deleteGeneralInfo,
  updateGeneralInfo,
} from '../../../redux/generalInformation/actions'
import { updateMetrics } from '../../../redux/goals/actions'
import { setActiveAvailability, setAvailabilities } from '../../../redux/availabilities/actions'
import { setChangeStatus } from '../../../redux/changeStatus/actions'

import StoreContext from '../../../store/Context'
import storage from '../../../utils/storage'

import Audio from '../../atoms/feedback/audio'
import InsightsEngine from '../../atoms/insights/insightsEngine'

import {
  findIdleMsg,
  getTriggers,
  shouldUpdateIdleMsg,
} from '../../../utils/messages'
import { isArray, objLength, isValidToken } from '../../../utils/customMethods'
import * as signalR from '@microsoft/signalr'
import saveUserEvents from '../../../utils/saveUserEvents'

import messagesAPI from '../../../services/messages'
import api from '../../../services/api'
import messagesCheck from '../../../utils/messagesCheck'
import { getChatInfo } from '../../../utils/chatInfo'

import { cockpit, cockpitSkills } from '../../../utils/cockpit'
import { setLogoutByManager } from '../../../redux/manageControl/actions'

const SignalR = () => {
  const theme = useTheme()
  const { token } = useContext(StoreContext)
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const user = useSelector((state) => state.user)
  const availabilities = useSelector((state) => state.availabilities.data)
  const conversations = useSelector(
    (state) => state.conversations.conversations
  )
  const tabList = useSelector((state) => state.tabList)
  const messages = useSelector((state) => state.conversations.messages)
  const activeConversation = useSelector((state) => state.activeConversation)
  const generalInformation = useSelector((satate) => satate.generalInformation)
  const [conn, setConn] = useState(null)
  const [status, setStatus] = useState(null)
  const [payload, setPayload] = useState(null)
  const [availability, setAvailability] = useState(null)
  const [loadingChats, setLoadingChats] = useState(false)

  const tag = {
    newChat: 'newMessageQueue',
    newClientMessage: 'newMessageTelegram',
    newPrivateMessage: 'newMessagePrivate',
    newManagerMessage: 'newMessageFromManager',
    newAgentMessage: 'newMessageFromAgent',
    closedByClient: 'closeConversationByClient',
    closedByAgent: 'closeConversationByAgent',
    closedByManager: 'closeConversationByManager',
    closedBySystem: 'closeConversationBySystem',
    transferByAgent: 'transferConversationByAgent',
    transferByManager: 'transferConversationByManager',
    transferBySystem: 'transferConversationBySystem',
    inactiveBySystem: 'inactiveConversationBySystem',
    joinManager: 'managerJoinToConversation',
    endPosChat: 'endPosChat',
    changeAvailability: 'changeAvailability',
    onReconnecting: 'onReconnecting',
    onReconnected: 'onReconnected',
    onClose: 'onClose',
    logoutByManager: 'logoutByManager',
  }

  const run = async (action) => {
    const actions = {
      start: () => conn.start(),
      stop: () => conn.stop(),
    }

    try {
      setStatus('connecting')
      await actions[action]()
      setStatus(conn?.connectionState.toLowerCase())
    } catch (e) {
      setStatus(conn?.connectionState.toLowerCase())
    }
  }
  const setNewChat = (chat) => {
    chat.message?.forEach((msg) => {
      msg.unread = true
    })
    chat.lastMessage.sentTime = new Date();
    chat.lastMessage.by = "agent";

    // Set idle message
    chat.idleMessage = findIdleMsg(chat)

    // Set status triggers
    chat.statusTriggers = getTriggers(chat)

    chat.uiMemo = {
      color: theme.palette.random[Math.floor(Math.random() * (14 - 0 + 1) + 0)],
    }

    // Set related components state
    chat.relatedComponents = {
      currentChat: {
        message: '',
        scroll: 0,
      },
      history: {
        isOpen: false,
        activeTab: 0,
        activeChat: { conversationId: null, activeMsg: null },
      },
      tools: {
        activeTab: 0,
      },
      data: {
        identification: {
          isOpen: true,
          relation: null,
        },
        client: {
          isOpen: true,
        },
      },
      notes: {
        insights: {
          isOpen: true,
        },
        favoriteMessages: {
          isOpen: true,
        },
        generalInfo: {
          isOpen: true,
          tabs: [],
        },
      },
      usability: {
        isOpen: false,
        url: null,
      },
    }

    // TODO: temporary. Adding sentTime in lastMessage (waiting for backend update)
    if (!chat.lastMessage?.sentTime) {
      chat.lastMessage.sentTime = chat.lastMessage?.time
    }

    dispatch(setConversations(chat))
    try {
      Audio.play('newConversation')
    } catch (error) {
      console.log('Error on play audio', error)
    }
  }

  const newConversation = async (chat) => {
    try {
      if (chat) {
        const { conversationId, consumer, skillId } = chat
        const isValid = cockpitSkills.includes(skillId)

        if (isValid) {
          const body = [
            {
              conversationId,
              nomeSolicitante: consumer?.name,
              arriving: true,
              matricula: user?.extra_matricula,
            },
          ]

          await cockpit(body)
        }
        setNewChat(chat)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const newMessage = (data, type) => {
    if (!data) return;
    if (!data.conversationId) return;

    try {
      let chatId = data.conversationId
      let chat = conversations[chatId]
      let joinUserToChat = false

      if (chat && isArray(messages[chatId])) {
        if (chat.status === 'active' || chat.status === 'inactive') {
          switch (type) {
            case 'client':
              // Update conversation status
              chat.status = 'active'
              break
            case 'private': {
              joinUserToChat = !chat.users?.some((userInChat) => userInChat.user_id === data.user.user_id)

              break
            }
            default:
              break
          }

          if (shouldUpdateIdleMsg(chat, chat.lastMessage)) {
            // Update idle message
            chat.idleMessage = chat.lastMessage

            // Update status triggers
            chat.statusTriggers = getTriggers(chat) ?? chat?.statusTriggers
          }
        }

        dispatch({
          type: 'ADD_NEW_USER_MESSAGE',
          payload: {
            chatId: data.conversationId,
            messages: data.message,
            conversationData: chat,
            user: data.user,
            joinUserToChat,
          }
        })

        try {
          Audio.play('newMessage')
        } catch (error) {
          console.log('Error on play audio', error)
        }
      } else {
        messagesAPI({
          method: 'get',
          url: `/tech4h-messages/conversation?id=${chatId}`,
        })
          .then((response) => {
            let chat = response?.data?.data
            if (chat) {
              setNewChat(chat)
            }
          })
          .catch((error) => {
            console.error(error)
          })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const changeAvailabilityAgent = (data) => {
    const avail = [...availabilities]

    let checkAvailability = avail?.find((el) => el.id === data.status)
    if (checkAvailability) {
      avail?.map((el) => {
        el.id === data.status ? (el.default = '1') : (el.default = '0')
        if (el && el.children) {
          el.children.map((children) => {
            children.default = '0'
          })
        }
      })
    } else {
      avail?.map((el) => {
        el.default = el?.children?.some(
          (children) => children.id === data.status
        )
          ? '1'
          : '0'
        el?.children?.map((children) => {
          children.default = children.id === data.status ? '1' : '0'
        })
      })
    }

    let availabilityChildren = avail[1]?.children?.find(
      (children) => children.default === '1'
    )

    if (!availabilityChildren) {
      availabilityChildren = avail[2]?.children?.find(
        (children) => children.default === '1'
      )
    }

    dispatch(setAvailabilities([...avail]))
    dispatch(setChangeStatus(true))
    setTimeout(() => dispatch(updateMetrics()), 1000)
  }

  const getClosedBy = (data, target, chat) => {
    const obj = {
      closeConversationByAgent: chat?.users?.reverse()?.[0]?.user_name,
      closeConversationByManager: data?.manager,
      closeConversationByClient: t(`commons.client`),
      closeConversationBySystem: t(`commons.system`),
    }

    return obj[target]
  }

  const closeConversation = async (data, target) => {
    try {
      const chatId = data?.conversationId
      const conversationTab = data?.conversationTab || null
      const chat = conversations[chatId]
      const generalInfo = generalInformation.conversations[chatId]
      const { hasPosChat } = data || {}
      const status = hasPosChat ? 'pos-chat' : 'closed'

      if (chat) {
        const { conversationId, consumer, skillId } = chat
        const isValid = cockpitSkills.includes(skillId)

        if (isValid) {
          const body = [
            {
              conversationId,
              nomeSolicitante: consumer?.name,
              arriving: false,
              matricula: user?.extra_matricula,
            },
          ]

          await cockpit(body)
        }

        // Update conversation attributes
        chat.status = status
        chat.end = new Date()
        if(conversationTab){
          chat.conversationTab = conversationTab
        }else{
          chat.pendingTab = true
        }
        

        if (tabList?.conversations?.[conversationId]) {
          tabList.conversations[conversationId] = null
        }

        dispatch(setConversations(chat))

        // Update active conversation if it's the same chat
        if (activeConversation === chat.conversationId) {
          dispatch(setActiveConversation(null))
        }

        // Update general information attributes
        if (generalInfo) {
          generalInfo.chatInfo.status = status
          generalInfo.chatInfo.end = new Date()
          generalInfo.chatInfo.closedBy = getClosedBy(data, target, chat)

          const obj = {
            chatId,
            data: generalInfo,
          }
          dispatch(updateGeneralInfo(obj))
        }
        
        // Update metric
        dispatch(updateMetrics())
        try {
          Audio.play('closeConversation')
        } catch (error) {
          console.log('Error on play audio', error)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const transferConversation = async (data) => {
    try {
      let chatId = data?.conversationId
      let chat = conversations[chatId]

      if (chat && isArray(messages[chatId])) {
        const { conversationId, consumer, skillId } = chat
        if (activeConversation === chatId) {
          dispatch(setActiveConversation(null))
        }
        dispatch(deleteConversations(chatId))

        const isValid = cockpitSkills.includes(skillId)

        if (isValid) {
          const body = [
            {
              conversationId,
              nomeSolicitante: consumer?.name,
              arriving: false,
              matricula: user?.extra_matricula,
            },
          ]

          await cockpit(body)
        }
        try {
          Audio.play('closeConversation')
        } catch (error) {
          console.log('Error on play audio', error)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const inactiveConversation = (data) => {
    try {
      let chatId = data?.conversationId
      let chat = conversations[chatId]

      if (chat && chat.status !== 'inactive') {
        chat.status = 'inactive'
        dispatch(setConversations(chat))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const joinManager = (data) => {
    try {
      const chatId = data?.conversationId
      let chat = conversations[chatId]
      const user = data?.user

      if (isArray(chat?.users) && user && !alreadyJoined) {
        chat.users.push(user)
        dispatch(setConversations(chat))
      }
    } catch (error) {
      console.error(error)
    }
  }
  const endPosChat = (data) => {
    try {
      const chatId = data?.conversationId
      const chat = conversations[chatId]

      if (chat) {
        // Update conversation attributes
        chat.status = 'closed'
        dispatch(setConversations(chat))

        dispatch(deleteGeneralInfo({ chatId }))
        if (activeConversation === chat.conversationId) {
          getChatInfo(chatId, dispatch)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onReconnecting = (error) => {
    setStatus(conn?.connectionState.toLowerCase())
    console.log(`Connection lost due to error. Reconnecting.`, error)
    checkMsgs()
  }

  const onReconnected = (connectionId) => {
    setStatus(conn?.connectionState.toLowerCase())
    console.log(
      `SignalR connection reestablished. Connected with connectionId.`,
      connectionId
    )
    const objAlert = {
      title: t('alerts.disconnect.title'),
      text: t('alerts.disconnect.text'),
    }
    setLoadingChats(objAlert)
    handleRefreshChats()
  }

  const onClose = (error) => {
    setStatus(conn?.connectionState.toLowerCase())
    console.error(
      `SignalR connection closed due to error. Try refreshing this page to restart the connection`,
      error
    )
  }

  const logoutUserByManager = (payload) => {
    dispatch(setLogoutByManager());
    setConn(null)
    setPayload(null)
    setStatus('disconnected')
    dispatch(setActiveAvailability(null))
  }

  const setData = (target, data) => {
    if (target) {
      let payload = {
        target: target,
        data: data,
      }
      setPayload(payload)
    }
  }

  const socketMethods = (conn) => {
    if (tag && conn) {
      conn.onreconnecting((e) => setData(tag.onReconnecting, e))
      conn.onreconnected((e) => setData(tag.onReconnected, e))
      conn.onclose((e) => setData(tag.onClose, e))

      conn.on(tag.newChat, (e) => setData(tag.newChat, e))
      conn.on(tag.newClientMessage, (e) => setData(tag.newClientMessage, e))
      conn.on(tag.newManagerMessage, (e) => setData(tag.newManagerMessage, e))
      conn.on(tag.newAgentMessage, (e) => setData(tag.newAgentMessage, e))
      conn.on(tag.newPrivateMessage, (e) => setData(tag.newPrivateMessage, e))
      conn.on(tag.closedByClient, (e) => setData(tag.closedByClient, e))
      conn.on(tag.closedByAgent, (e) => setData(tag.closedByAgent, e))
      conn.on(tag.closedBySystem, (e) => setData(tag.closedBySystem, e))
      conn.on(tag.closedByManager, (e) => setData(tag.closedByManager, e))
      conn.on(tag.transferByAgent, (e) => setData(tag.transferByAgent, e))
      conn.on(tag.transferByManager, (e) => setData(tag.transferByManager, e))
      conn.on(tag.transferBySystem, (e) => setData(tag.transferBySystem, e))
      conn.on(tag.inactiveBySystem, (e) => setData(tag.inactiveBySystem, e))
      conn.on(tag.joinManager, (e) => setData(tag.joinManager, e))
      conn.on(tag.endPosChat, (e) => setData(tag.endPosChat, e))
      conn.on(tag.changeAvailability, (e) => setData(tag.changeAvailability, e))
      conn.on(tag.logoutByManager, (e) => setData(tag.logoutByManager, e))
    }
  }

  const handlePayload = useCallback(
    (payload) => {
      if (tag && payload) {
        saveUserEvents(payload.target, payload.data)
        switch (payload.target) {
          case tag.newChat:
            newConversation(payload.data)
            break
          case tag.newClientMessage:
            newMessage(payload.data, 'client')
            break
          case tag.newAgentMessage:
            newMessage(payload.data)
            break
          case tag.newManagerMessage:
            newMessage(payload.data)
            break
          case tag.newPrivateMessage:
            newMessage(payload.data, 'private')
            break
          case tag.closedByClient:
            closeConversation(payload.data, payload.target)
            break
          case tag.closedByAgent:
            closeConversation(payload.data, payload.target)
            break
          case tag.closedByManager:
            closeConversation(payload.data, payload.target)
            break
          case tag.closedBySystem:
            closeConversation(payload.data, payload.target)
            break
          case tag.transferByAgent:
          case tag.transferByManager:
          case tag.transferBySystem:
            transferConversation(payload.data)
            break
          case tag.inactiveBySystem:
            inactiveConversation(payload.data)
            break
          case tag.joinManager:
            joinManager(payload.data)
            break
          case tag.endPosChat:
            endPosChat(payload.data)
            break
          case tag.changeAvailability:
            changeAvailabilityAgent(payload.data)
            break

          case tag.onReconnecting:
            onReconnecting(payload.data)
            break
          case tag.onReconnected:
            onReconnected(payload.data)
            break
          case tag.onClose:
            onClose(payload.data)
            break
          case tag.logoutByManager:
            logoutUserByManager(payload.data)
            break
          default:
            break
        }
      }
    },
    [payload]
  )

  let retry = 0
  const routineCheckConversation = async () => {
    if (history.location.pathname === '/dashboard') {
      try {
        const response = await api.post(`/conversations-check`, {
          conversations: Object.entries(conversations)
            .filter(
              (e) => e[1].status === 'active' || e[1].status === 'inactive'
            )
            .map((e) => e[0]),
        })
        if (response.status !== 200) throw response.data
        if (!response?.data?.data?.isOk) {
          if (retry < 2) {
            retry += 1
          } else {
            const objAlert = {
              title: t('alerts.lostChat.title'),
              text: t('alerts.lostChat.text'),
            }
            setLoadingChats(objAlert)
            handleRefreshChats()
            retry = 0
          }
        } else {
          retry = 0
        }
      } catch (error) {
        const message = error?.response?.data?.message?.[0]?.value ?? 'error'
        console.error(`[Conversations check routine error]: ${message}`)
      }
    }
  }

  const checkMsgs = async () => {
    const isOk = await messagesCheck(messages)
    if (!isOk) {
      handleRefreshChats()
    }
  }

  const handleInsight = (payload) => {
    return payload?.target === tag.newChat ||
      payload?.target === tag.newClientMessage ? (
      <InsightsEngine payload={payload.data} />
    ) : null
  }  

  const handleRefreshChats = () => {
    messagesAPI({
      method: 'get',
      url: `/tech4h-messages/conversation`,
    })
      .then(async (response) => {
        let data = response.data.data

        const leaving = await cockpit(null, data, conversations, user)
        if (leaving.length > 0) {
          for (let i = 0; i < leaving.length; i++) {
            if (activeConversation === leaving[i]) {
              dispatch(setActiveConversation(null))
            }
            dispatch(deleteConversations(leaving[i]))
          }
        }

        for (let x = 0; x < data.length; x++) {
          // Set idle message
          data[x].idleMessage = findIdleMsg(data[x])

          // Set status triggers
          data[x].statusTriggers = getTriggers(data[x])

          // TODO: temporary. Adding sentTime in lastMessage (waiting for backend update)
          if (!data[x].lastMessage?.sentTime) {
            data[x].lastMessage.sentTime = data[x].lastMessage?.time
          }

          data[x].uiMemo = {
            color:
              theme.palette.random[
                Math.floor(Math.random() * (14 - 0 + 1) + 0)
              ],
          }

          // Set related components state
          data[x].relatedComponents = {
            currentChat: {
              message: '',
              scroll: 0,
            },
            history: {
              isOpen: false,
              activeTab: 0,
              activeChat: { conversationId: null, activeMsg: null },
            },
            tools: {
              activeTab: 0,
            },
            data: {
              identification: {
                isOpen: true,
                relation: null,
              },
              client: {
                isOpen: true,
              },
            },
            notes: {
              insights: {
                isOpen: true,
              },
              favoriteMessages: {
                isOpen: true,
              },
              generalInfo: {
                isOpen: true,
                tabs: [],
              },
            },
            usability: {
              isOpen: false,
              url: null,
            },
          }

          const hasConversation = conversations?.[data[x]?.conversationId]

          if (hasConversation && hasConversation.status === data[x]?.status) {
            dispatch(setMessages(data[x]))
          } else {
            dispatch(setConversations(data[x]))
          }
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setLoadingChats(false)
      })
  }

  const renderDisconnectDialog = (objAlert) => (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={objAlert}
      disableBackdropClick={true}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>{objAlert.title}</div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{objAlert.text}</Typography>
      </DialogContent>
      <LinearProgress />
    </Dialog>
  )

  useEffect(() => {
    Audio.setVolume(storage.get('volume') ?? 100)
  }, [])

  useEffect(() => {
    const interval = setInterval(routineCheckConversation, 10000)
    return () => clearInterval(interval)
  }, [conversations])

  useEffect(() => {
    if (!isValidToken(token)) {
      run('stop')
      setConn(null)
    } else if (objLength(user) > 0 && !conn) {
      const hubName = 'techcdp'
      const signalrId = user?.signalr ?? new Date().getTime()
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(
          `${process.env.REACT_APP_MESSAGES}/tech4h-messages/connection/user?hubname=${hubName}&signalrId=${signalrId}`,
          { accessTokenFactory: () => token?.substr(7, token.length) }
        )
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Debug)
        .build()
      console.log(signalR.VERSION)
      setConn(connection)
    }
  }, [user, token])

  useEffect(() => {
    if (conn) {
      socketMethods(conn)
      status !== 'connected' && run('start')
    }
  }, [conn])

  useEffect(() => {
    if (status) {
      dispatch(setConnSignalR(status))
    }
  }, [status])

  useEffect(() => {
    payload && handlePayload(payload)
  }, [payload])

  useEffect(() => {
    setAvailability(availabilities?.find((el) => el?.default === '1')?.id)
  }, [availabilities, setAvailability])

  // TODO: Don't foget!! refactor this component. Return to the original version (GP - legacy)
  return (
    // <WindowFocus
    //   onFocus={() => onFocus(availability, checkStatus)}
    //   onBlur={() => onBlur(availability, checkStatus)}
    //   reload={availability}
    // >
    <>
      {handleInsight(payload)}
      {renderDisconnectDialog(loadingChats)}
    </>
    // </WindowFocus>
  )
}

export default SignalR
